import React, { useMemo, useState, Fragment, useContext, createContext, PropsWithChildren } from "react";
import styled from "styled-components";
import { ArrowNext } from 'assets/images/ArrowNext'

interface ITableContext {
  headers: string[],
  tooltips: string[],
  items: any[]
}

const TableContext = createContext<ITableContext>({
  headers: [],
  items: [],
  tooltips: [],
})

interface ExpansionTableProps<T> extends PropsWithChildren {
  /**
   * Selected property name to group
   */
  groupBy: string,

  /**
   * Fallback group name if selected property name does not exist in table item or toFallbackGroup value is set
   */
  fallbackGroupName: string,

  /**
   * List of name to grouped into fallback group
   */
  toFallbackGroup?: string[],

  /**
   * Data item for table. List of objects
   */
  items: T[],

  /**
   * Table header
   */
  headers: string[],

  /**
   * Tooltips for table header. Header and tooltip use same index
   */
  tooltips: string[],
}

export const ExpansionTable = <T extends { [key: string]: any },>(props: ExpansionTableProps<T>): JSX.Element => {
  const [groupBy] = useState(props.groupBy)
  const [items] = useState(props.items)
  const [fallbackGroupName] = useState(props.fallbackGroupName)
  const [toFallbackGroup] = useState(props.toFallbackGroup ?? [])

  const groupDictionary = useMemo(() => {
    const uniqueList: { [key: string]: T[] } = {}
    const unGroupedList: T[] = []
    // Create empty array for defined or undefined group and add product to group according to group id
    // Any item property value in toFallbackGroup list will be grouped into fallback group
    items.forEach(product => {
      const foundIndex = product[groupBy] !== undefined
        ? toFallbackGroup.indexOf(product[groupBy].toString())
        : 0
      if (product[groupBy] !== undefined && foundIndex < 0) {
        if (uniqueList[product[groupBy]] === undefined) uniqueList[product[groupBy]] = []
        uniqueList[product[groupBy]].push(product)
      }
      else unGroupedList.push(product)
    })

    const sortedUniqueList: { [key: string]: T[] } = {...uniqueList}
    
    if (unGroupedList.length > 0) sortedUniqueList[fallbackGroupName] = unGroupedList
    
    return sortedUniqueList
  }, [items])

  return (
    <TableContext.Provider value={{
      headers: props.headers,
      items: items,
      tooltips: props.tooltips
    }}>
      <div>
        {
          Object.entries(groupDictionary).map(([groupName, content]) =>
            <ExpansionTableGroup
              title={groupName}
              content={content}
              children={props.children}
              key={`group_${groupName}`}
            />
          )
        }
      </div>
    </TableContext.Provider>
  )
}

interface ExpansionTableGroupProps<T> extends PropsWithChildren {
  title: string,
  content: T[] // array of objects
}

const ExpansionTableGroup = <T,>(props: ExpansionTableGroupProps<T>): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(false)
  function toggle() {
    setIsExpanded(!isExpanded)
  }
  const tableContext = useContext(TableContext)
  return (
    <>
      <StyledTableGroupHeader onClick={toggle} className="expansion-table-group-header">
        <GroupTitle className="expansion-table-group-title">{props.title}</GroupTitle>
        <Spacer />
        <ArrowWrapper>
          {isExpanded ? 'ー' : '＋'}
          {/* <ArrowNext /> */}
        </ArrowWrapper>
      </StyledTableGroupHeader>
      <StyledTableGroupContent isVisible={isExpanded} className="expansion-table-group-content-wrapper">
        {
          isExpanded
            ? <Table>
              <thead>
                <TableHeader>
                  {
                    tableContext.headers.map((header, index) => {
                      return (
                        <Column key={`th_${header}`}>
                          {header}
                          <ToolTip className="tooltip">
                            {tableContext.tooltips[index]}
                          </ToolTip>
                        </Column>
                      )
                    })
                  }
                </TableHeader>
              </thead>
              <tbody className="expansion-table-group-content-body">
                {
                  Object.values(props.content).map((item, rowIndex) => {
                    return (
                      <tr key={`content_${rowIndex}`} className="expansion-table-group-content-row">
                        {
                          React.Children.map(props.children, ((child, childIndex) => {
                            if (React.isValidElement(child)) {
                              return (
                                <Fragment key={`content_${rowIndex}_${childIndex}`}>
                                  {React.cloneElement(child, { ...child.props, content: item, className: "expansion-table-group-content-cell" })}
                                </Fragment>
                              )
                            }
                            else return null
                          }))
                        }
                      </tr>
                    )
                  })
                }
              </tbody>
            </Table>
            : null
        }
      </StyledTableGroupContent>
    </>
  )
}

interface ExpansionTableGroupContentProps<T> {
  content?: T,
  children: {
    content: (value: T) => React.ReactNode
  }
}

export const ExpansionTableGroupContent = <T,>(props: ExpansionTableGroupContentProps<T>): JSX.Element => {
  const { content } = props.children
  return (
    <>
      {
        props.content
          ? content(props.content)
          : null
      }
    </>
  )
}

const ArrowWrapper = styled.div<{ isRotated?: boolean }>`
  display: flex;
  margin: 0 12px;
  width: 5px;
  justify-items: center;
  transform: rotate(${props => props.isRotated ? '90deg' : '0deg'});
`

const Spacer = styled.div`
  flex-grow: 1;
`;

const StyledTableGroupHeader = styled.div`
  display: flex;
  border-top: 0.1rem solid #e6e6e6;
  border-bottom: 0.1rem solid #e6e6e6;
  padding: 10px 12px;
  cursor: pointer;
  transition: all linear 150ms;
  &:hover {
    background-color: #f4f4f4;
  }
`;
const StyledTableGroupContent = styled.div<{ isVisible?: boolean }>`
  border: 0 solid #e6e6e6;
  padding: ${props => props.isVisible ? '10px 0px' : '0 0px'};
  height: ${props => props.isVisible ? 'auto' : '0'};
  transition: linear 100ms;
`;

const GroupTitle = styled.span<{ textAlign?: string }>`
  text-align: ${props => props.textAlign ?? 'center'};
  vertical-align: middle;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
`;

export const Table = styled.table`
  position: relative;
  width: 100%;
  margin-bottom: 0;
  border: 0.1rem solid #e6e6e6;
`;

export const TableHeader = styled.tr`
  background: #f2f7fb;
`;

export const Column = styled.th`
  padding: 14px;
  text-align: center;
  vertical-align: middle;
  font-size: 11px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  border: 0.1rem solid #e6e6e6;
  user-select: none;
  transition: all 0.15s linear;
  .tooltip {
    opacity: 0;
  }
  :hover .tooltip {
    opacity: 1;
  }
`;

export const ToolTip = styled.span`
  box-sizing: border-box;
  position: absolute;
  width: 100%;
  bottom: 100%;
  left: 0px;
  border: 0.1rem solid rgba(0, 0, 0, 0.1);
  padding-top: 1rem;
  padding-bottom: 1rem;
  padding-left: 1.414rem;
  padding-right: 1.414rem;
  margin-bottom: 1rem;
  text-align: left;
  background: #fafafa;
  border-radius: 4px;
  z-index: 9999;
  pointer-events: none;
  transition: all 0.15s linear;
`;

export const Item = styled.td<{ textAlign?: string, width?: string }>`
  padding: 14px;
  text-align: ${props => props.textAlign ?? 'left'};
  ${props => props.width ? `width: ${props.width};` : ''}
  vertical-align: middle;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  border: 0.1rem solid rgba(0, 0, 0, 0.1);
  padding: 10px 12px;
`;

export const Loading = styled.p`
  width: 50px;
  height: 50px;
  margin: 40px auto;
  border: 3px solid #e6e6e6;
  border-top: 3px solid transparent;
  border-radius: 50%;
  animation: spin 0.8s linear 0s infinite;
`;

export const Message = styled.p`
  margin: 50px;
  text-align: center;
`;

export const getDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
};
